import React, { useCallback, useEffect, useState } from "react";
import "./Result.scss";
import pusher from "@utils/Pusher";
import LinkedInLogo from "@assets/img/logo/Linkedin-color.svg";
import { Button, Col, Pagination, Progress, Row, Skeleton } from "antd";
import Helpers from "@utils/Helpers";
import ScatterChart from "@components/ScatterChart/ScatterChart";
import CandidateDetailModal from "@components/CandidateDetailModal/CandidateDetailModal";
import businessLogicApi from "services/BusinessLogicApi";

import {
  ArrowRightOutlined,
  EuroOutlined,
  FieldTimeOutlined,
  UserOutlined,
  WarningFilled,
} from "@ant-design/icons";
import BarChart from "@components/BarChart/BarChart";
import { IResult } from "@utils/Interfaces";

const Result: React.FC<IResult> = ({
  campaign,
  visible,
  campaignCurrentStep,
  pusherChannelCrawl,
  pusherEventCrawl,
  jobDescription,
}) => {
  const [linkedinUrlData, setLinkedinUrlData] = useState([]);
  const [linkedinProfileData, setLinkedinProfileData] = useState([]);
  const [linkedinEvaluateData, setLinkedinEvaluateData] = useState([]);
  const [isCandidateDetailModal, setIsCandidateDetailModal] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<string>("");
  const [isCrawlEvalute, setIsCrawlEvaluate] = useState(false);
  const [selectedList, setSelectedList] = useState<any>([]);
  const [notSelectedList, setNotSelectedList] = useState<any>([]);
  const [excludeList, setExcludeList] = useState<any>([]);
  const notification = Helpers.notification;
  const businessPayload = Helpers.businessPayload;

  // LISTEN PUSHER 110
  useEffect(() => {
    if (visible) {
      if (
        campaign.status_110 == "Not started" &&
        campaign.status_210 == "Not started" &&
        campaign.status_310 == "Not started"
      ) {
        const pusherChannelGroup = {
          pusher_channel_name: pusherChannelCrawl,
          pusher_event_name: pusherEventCrawl,
        };
        pusher(pusherChannelGroup, (message) => {
          setLinkedinUrlData(message);
          getPusherInfoForCrawlProfile(message);
        });
      } else {
        getDataForScripts();
      }
    }
  }, [visible]);

  // WHEN 210 DATA LENGTH = 110 DATA LENGTH => SET CRAWL EVALUATION
  useEffect(() => {
    if (
      linkedinProfileData.length > 0 &&
      linkedinProfileData.length === linkedinUrlData.length &&
      campaign.status_310 == "Not started"
    ) {
      setIsCrawlEvaluate(true);
    }
  }, [linkedinProfileData]);

  // WHEN 210 DATA LENGTH = 110 DATA LENGTH => CRAWL EVALUATION
  useEffect(() => {
    if (isCrawlEvalute) {
      crawlEvaluate();
    }
  }, [isCrawlEvalute]);

  // GET AND LISTEN PUSHER 210
  const getPusherInfoForCrawlProfile = async (message: any) => {
    try {
      const response = await businessLogicApi.Script_210(
        businessPayload.script(210, {
          candidates: message,
          campaign_id: campaign.campaign_id,
        }),
        campaign.campaign_id
      );
      if (response.status === 200) {
        pusher(response.data, (message: any) => {
          if (!message.status) {
            console.log(message);
            setLinkedinProfileData((prevData): any => [...prevData, message]);
          }
        });
      }
    } catch (error) {
      notification.error("Something went wrong in server");
    }
  };

  // SUMMARY DATA FROM 210
  const payloadFormat = (): any[] => {
    const payload: any[] = [];

    linkedinProfileData.map((item: any) => {
      const payloadItem = {
        full_name: item.details.name,
        url: item.url,
        slug: item.slug,
        position: item.details.position || "N/A",
        education: item.details.crawl_profile_educations
          ? item.details.crawl_profile_educations.map((edu: any) => ({
              school: edu.school_name,
              degree_type: edu.degree_type,
              period: edu.time_period,
            }))
          : [],
        experience: item.details.crawl_profile_exps
          ? item.details.crawl_profile_exps.map((exp: any) => ({
              company: exp.company_name,
              title: exp.job_title,
              period: exp.time_period,
              description: null,
            }))
          : [],
        languages: item.details.crawl_profile_languages || [""],
        skills: item.details.crawl_profile_skills
          ? item.details.crawl_profile_skills.map(
              (skill: any) => skill.skill_name
            )
          : [],
      };
      payload.push(payloadItem);
    });
    return payload;
  };

  // GET AND LISTEN PUSHER 310
  const crawlEvaluate = async () => {
    const payload = payloadFormat();

    try {
      const response = await businessLogicApi.Script_310(
        businessPayload.script(310, {
          candidates: payload,
          jd: jobDescription,
          campaign_id: campaign.campaign_id,
        }),
        campaign.campaign_id
      );
      if (response.status === 200) {
        pusher(response.data, (message) => {
          setLinkedinEvaluateData((prevData): any => [...prevData, message]);
        });
      }
    } catch (error) {
      notification.error("Something wrong in Server");
    }
  };

  // CALCULATE RATIO OF CANDIDATE LISTS
  const calculateRatio = useCallback(() => {
    const totalLength =
      selectedList.length + notSelectedList.length + excludeList.length;

    const selectedListRatio = (selectedList.length / totalLength) * 100;
    const notSelectedRatio = (notSelectedList.length / totalLength) * 100;
    const excludeRatio = (excludeList.length / totalLength) * 100;

    return {
      selectedListRatio: selectedListRatio,
      notSelectedRatio: notSelectedRatio,
      excludeRatio: excludeRatio,
    };
  }, [selectedList, notSelectedList, excludeList]);

  // HANDLE GET DATA FROM ENDPOINT VIA SCRIPT
  const getDataForScripts = async () => {
    const endpoints = [
      {
        name: 110,
        condition: campaign.status_110 === "Done",
        call: businessLogicApi.GetData110,
      },
      {
        name: 210,
        condition: campaign.status_210 === "Done",
        call: businessLogicApi.GetData210,
      },
      {
        name: 310,
        condition: campaign.status_310 === "Done",
        call: businessLogicApi.GetData310,
      },
    ];

    const endpointsToCall = endpoints.filter((ep) => ep.condition);

    try {
      const responses = await Promise.all(
        endpointsToCall.map((ep) => ep.call(campaign.campaign_id))
      );

      const result = responses.map((response, index) => ({
        name: endpointsToCall[index].name,
        data: response,
      }));
      handleGetDataSuccess(result);
    } catch (error) {
      console.error("Error calling endpoints:", error);
      notification.error("Something wrong in server");
    }
  };

  const handleGetDataSuccess = (results: any) => {
    results.forEach((result: any) => {
      if (result.name == 110) {
        setLinkedinUrlData(result.data.data[0].urls);
        if (campaign.status_210 === "Not started") {
          getPusherInfoForCrawlProfile(result.data.data[0].urls);
        } else if (campaign.status_210 === "Running") {
          const pusherChannelGroup = {
            pusher_channel_name: "private-heppai-container-0",
            pusher_event_name: `campaign-${campaign.campaign_id}-script210`,
          };
          pusher(pusherChannelGroup, (message) => {
            console.log(210, message);
          });
        }
      }
      if (result.name == 210) {
        setLinkedinProfileData(result.data.data);
        if (campaign.status_310 === "Running") {
          const pusherChannelGroup = {
            pusher_channel_name: "private-heppai-container-0",
            pusher_event_name: `campaign-${campaign.campaign_id}-script310`,
          };
          pusher(pusherChannelGroup, (message) => {
            console.log(310, message);
          });
        }
      } else {
        setLinkedinEvaluateData(result.data.data);
      }
    });
  };

  return (
    <>
      <div className={`Result ${visible !== true ? "d-none" : ""}`}>
        <Row className="mt-3" gutter={12}>
          <Col span={14}>
            <Row className="h-100" gutter={12}>
              <Col span={12}>
                <div className="campaign-overview">
                  <div className="overview-status-date">
                    <div className="status">
                      <FieldTimeOutlined />
                    </div>
                    <div className="date">12/11/2024</div>
                  </div>
                  <div className="overview-notice">
                    <div className="notice">
                      <WarningFilled /> 2 days left
                    </div>
                    <div className="description">
                      You've reached{" "}
                      <span className="overview-percent">46%</span> of your goal
                    </div>
                    <Button type="link" className="enhance-action">
                      Enhance your search <ArrowRightOutlined />
                    </Button>
                  </div>
                  <div className="overview-benchmark">
                    <div className="profile-benchmark">
                      <div className="value">65</div>
                      <div className="process">
                        <Progress
                          percent={30}
                          showInfo={false}
                          strokeColor={"#5AC6C6"}
                        />
                      </div>
                      <div className="title">
                        <UserOutlined /> Profile
                      </div>
                    </div>
                    <div className="cost-benchmark mt-2">
                      <div className="value">200</div>
                      <div className="process">
                        <Progress
                          percent={80}
                          showInfo={false}
                          strokeColor={"#5AC6C6"}
                        />
                      </div>
                      <div className="title">
                        <EuroOutlined /> Cost
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div className="barchart-group">
                  <BarChart />
                  <div className="card-data flex-fill card-candidate-overview">
                    <div className="progress">
                      <div
                        className="progress-bar progress-bar-selected"
                        style={{
                          width: calculateRatio().selectedListRatio + "%",
                        }}
                      >
                        {selectedList.length}
                      </div>
                      <div
                        className="progress-bar progress-bar-excluded"
                        style={{ width: calculateRatio().excludeRatio + "%" }}
                      >
                        {excludeList.length}
                      </div>
                      <div
                        className="progress-bar progress-bar-not-selected"
                        style={{
                          width: calculateRatio().notSelectedRatio + "%",
                        }}
                      >
                        {notSelectedList.length}
                      </div>
                    </div>
                    <div className="progress-desc">
                      <div className="selected-desc">
                        <span></span> seleted
                      </div>
                      <div className="excluded-desc">
                        <span></span> excluded
                      </div>
                      <div className="not-seleted-desc">
                        <span></span> not-selected
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
          <Col span={10}>
            <div className="d-flex flex-column gap-3 h-100 scatter-group">
              <ScatterChart
                linkedinProfileData={linkedinProfileData}
                linkedinEvaluateData={linkedinEvaluateData}
              />
            </div>
          </Col>
        </Row>
        <div className="table-responsive CandidateTable mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <div className="candidate-select">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value={""}
                    />
                  </div>
                </th>
                <th>Candidate</th>
                <th>Linkedin</th>
                <th>Overall</th>
                <th>Experience</th>
                <th>Skills</th>
                <th>Languages</th>
                <th>Education</th>
              </tr>
            </thead>
            <tbody className={linkedinUrlData.length === 0 ? "d-none" : ""}>
              {linkedinUrlData.length > 0 &&
                linkedinUrlData.map((item: any, index: any) => {
                  var filteredProfile: any = linkedinProfileData.filter(
                    (ite: any) => ite.slug === item.slug
                  );
                  const filteredEvaluate: any = linkedinEvaluateData.filter(
                    (ite: any) => ite.slug === item.slug
                  );
                  return (
                    <tr
                      key={index}
                      onClick={(): void => {
                        setIsCandidateDetailModal(true);
                        setSelectedDetail(item.slug);
                      }}
                    >
                      <td>
                        <div className="candidate-select">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            value={""}
                          />
                        </div>
                      </td>
                      <td>
                        {linkedinProfileData.length > 0 ? (
                          filteredProfile.length === 0 ? (
                            <Skeleton active paragraph={{ rows: 1 }} />
                          ) : (
                            <div className="d-flex align-items-center gap-2">
                              <div className="flex-shrink-0">
                                <img
                                  src={filteredProfile[0].details.avatar}
                                  width={35}
                                  className="img-fluid rounded-circle"
                                  onError={(e: any) => {
                                    e.target.onerror = null;
                                    e.target.src =
                                      "https://static.vecteezy.com/system/resources/previews/009/292/244/non_2x/default-avatar-icon-of-social-media-user-vector.jpg";
                                  }}
                                />
                              </div>
                              <div className="flex-grow-1">
                                <div className="data-candidate-name">
                                  {filteredProfile[0].details.name}
                                </div>
                                <div className="data-candidate-position">
                                  {filteredProfile[0].details.position}
                                </div>
                              </div>
                            </div>
                          )
                        ) : (
                          <Skeleton active paragraph={{ rows: 1 }} />
                        )}
                      </td>
                      <td>
                        <a href={item.url} target="_blank">
                          <img src={LinkedInLogo} />
                        </a>
                      </td>
                      <td>
                        {linkedinEvaluateData.length > 0 ? (
                          filteredEvaluate.length === 0 ? (
                            <Skeleton active paragraph={{ rows: 1 }} />
                          ) : (
                            filteredEvaluate[0].overall
                          )
                        ) : (
                          <Skeleton active paragraph={{ rows: 1 }} />
                        )}
                      </td>
                      <td>
                        {linkedinEvaluateData.length > 0 ? (
                          filteredEvaluate.length === 0 ? (
                            <Skeleton active paragraph={{ rows: 1 }} />
                          ) : (
                            filteredEvaluate[0].experience
                          )
                        ) : (
                          <Skeleton active paragraph={{ rows: 1 }} />
                        )}
                      </td>
                      <td>
                        {linkedinEvaluateData.length > 0 ? (
                          filteredEvaluate.length === 0 ? (
                            <Skeleton active paragraph={{ rows: 1 }} />
                          ) : (
                            filteredEvaluate[0].skills
                          )
                        ) : (
                          <Skeleton active paragraph={{ rows: 1 }} />
                        )}
                      </td>
                      <td>
                        {linkedinEvaluateData.length > 0 ? (
                          filteredEvaluate.length === 0 ? (
                            <Skeleton active paragraph={{ rows: 1 }} />
                          ) : (
                            filteredEvaluate[0].languages
                          )
                        ) : (
                          <Skeleton active paragraph={{ rows: 1 }} />
                        )}
                      </td>
                      <td>
                        {linkedinEvaluateData.length > 0 ? (
                          filteredEvaluate.length === 0 ? (
                            <Skeleton active paragraph={{ rows: 1 }} />
                          ) : (
                            filteredEvaluate[0].education
                          )
                        ) : (
                          <Skeleton active paragraph={{ rows: 1 }} />
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
            <tbody className={linkedinUrlData.length > 0 ? "d-none" : ""}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item: any, index: any) => {
                return (
                  <tr key={index}>
                    <td>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </td>
                    <td>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </td>
                    <td>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </td>
                    <td>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </td>
                    <td>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </td>
                    <td>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </td>
                    <td>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </td>
                    <td>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </td>
                    <td>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="page-control">
          <Pagination className="pagination" defaultCurrent={1} total={50} />
          <Button className="btn-new-campaign">New Campaign</Button>
        </div>
      </div>
      <CandidateDetailModal
        isOpen={isCandidateDetailModal}
        cancel={() => setIsCandidateDetailModal(false)}
        candidateProfile={linkedinProfileData}
        candidateEvaluate={linkedinEvaluateData}
        setSelectedDetail={setSelectedDetail}
        selectedDetail={selectedDetail}
        selectedList={selectedList}
        setSelectedList={setSelectedList}
        notSelectedList={notSelectedList}
        setNotSelectedList={setNotSelectedList}
        excludeList={excludeList}
        setExcludeList={setExcludeList}
      />
    </>
  );
};

export default Result;
